import React, { useEffect, useState, useRef } from "react";
import "./InvoiceForm.scss";
import { getMyClients } from "../../services/api/MyClientsApi";
import { addInvoiceCompany } from "../../services/api/InvoicesApi";
import { useGlobalSettings } from "../../context/GlobalSettingsContext";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import CommonModal from "../../components/CommonModal/CommonModal";
import { MyClientForm } from "../../forms/MyClientForm/MyClientForm";
import { updateInvoiceSettings } from "../../services/api/GlobalSettingsApi";

export const InvoiceForm = () => {
  const { company } = useAuth();
  const navigate = useNavigate();

  const { globalSettings, setGlobalSettings } = useGlobalSettings();
  const invoiceSettings = globalSettings?.result?.[0]?.invoiceSettings || {};
  console.log(invoiceSettings, "invoiceSettingsinvoiceSettingsinvoiceSettings");
  const predefinedCount = invoiceSettings?.predefinedNo?.count || 0;
  const predefinedText = invoiceSettings?.predefinedNo?.text || "INV";
  const servicesList = invoiceSettings?.services || [];

  const [clients, setClients] = useState([]);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    invoiceNumber: `${predefinedText}/${predefinedCount + 1}`,
    issuedToType: "client",
    issuedTo: "",
    subtotal: 0,
    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: 0,
    totalAmount: 0,
    gstIncluded: invoiceSettings.gstIncluded,
  });

  const [addedServices, setAddedServices] = useState([]);
  const [currentService, setCurrentService] = useState({
    serviceId: "",
    name: "",
    description: "",
    rate: "",
    qty: "",
    amount: 0,
  });
  const [editingServiceIndex, setEditingServiceIndex] = useState(null);

  const [newServiceName, setNewServiceName] = useState("");
  const [isAddingService, setIsAddingService] = useState(false);

  // Add refs for focus management
  const issuedToRef = useRef(null);
  const serviceSelectRef = useRef(null);
  const quantityInputRef = useRef(null);
  const rateInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  const hasFocusedRef = useRef(false);
  const addServiceModalRef = useRef(null);
  const { showToast } = useToast();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showServiceModal && addServiceModalRef.current && !addServiceModalRef.current.contains(event.target)) {
        setShowServiceModal(false);
      }
    };

    if (showServiceModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showServiceModal]);


  // Auto-focus effect
  useEffect(() => {
    // Prevent multiple focus attempts
    if (hasFocusedRef.current) return;

    // Add a small delay to ensure DOM is ready and clients are loaded
    const focusTimeout = setTimeout(() => {
      const issuedToSelect = issuedToRef.current;
      if (issuedToSelect && !hasFocusedRef.current && clients.length > 0) {
        issuedToSelect.focus();
        hasFocusedRef.current = true;
      }
    }, 200);

    return () => clearTimeout(focusTimeout);
  }, [clients]);

  // Auto-focus service fields when client is selected
  useEffect(() => {
    if (formData.issuedTo && serviceSelectRef.current && addedServices.length === 0) {
      // Focus service dropdown when client is selected and no services added yet
      setTimeout(() => {
        serviceSelectRef.current?.focus();
      }, 100);
    }
  }, [formData.issuedTo, addedServices.length]);

  // Add Enter key submission functionality
  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      const target = e.target;
      const tagName = target.tagName.toLowerCase();
      const isSelect = tagName === 'select';
      const isTextarea = tagName === 'textarea';
      const isInModal = target.closest('.modal') || target.closest('[class*="modal"]') || target.closest('.confirmation-modal');
      const isButton = tagName === 'button';
      
      // Allow Enter in selects, textareas, modals, and buttons
      if (isSelect || isTextarea || isInModal || isButton) {
        return;
      }
      
      // For service form fields, handle Tab-like behavior instead of form submission
      if (target === serviceSelectRef.current && currentService.name) {
        e.preventDefault();
        quantityInputRef.current?.focus();
        return;
      }
      
      if (target === quantityInputRef.current && currentService.qty) {
        e.preventDefault();
        rateInputRef.current?.focus();
        return;
      }
      
      if (target === rateInputRef.current && currentService.rate) {
        e.preventDefault();
        descriptionInputRef.current?.focus();
        return;
      }
      
      if (target === descriptionInputRef.current) {
        e.preventDefault();
        // If current service is valid, add it
        if (isCurrentServiceValid()) {
          handleAddCurrentService();
          // Focus will be handled by the add service function
        }
        return;
      }
      
      // For main form submission
      e.preventDefault();
      if (isFormValid() && !isSaving) {
        handleSubmit(e);
      }
    }
  };

  // Focus management for service operations
  const focusAfterServiceOperation = (operation = 'add') => {
    setTimeout(() => {
      if (operation === 'add' || operation === 'edit' || operation === 'cancel') {
        // After adding, editing, or canceling, focus back to service select
        serviceSelectRef.current?.focus();
      } else if (operation === 'delete') {
        // After deleting, focus service select if no services left, otherwise focus the form
        if (addedServices.length === 0) {
          serviceSelectRef.current?.focus();
        }
      }
    }, 100);
  };

  useEffect(() => {
    const noPredefinedNo =
      !invoiceSettings?.predefinedNo?.text ||
      invoiceSettings?.predefinedNo?.text?.trim() === "";
    const noRecieptNo =
      !invoiceSettings?.recieptNo?.text ||
      invoiceSettings?.recieptNo?.text?.trim() === "";
    const noServices =
      !Array.isArray(invoiceSettings?.services) ||
      invoiceSettings.services.length === 0;

    if (noPredefinedNo || noServices) {
      showToast(
        "Predefined invoice number text and at least one service are required. Please configure them in settings.",
        "warning"
      );
      setTimeout(() => {
        navigate("/home/settings");
      }, 3000);
    }
    if (noRecieptNo) {
      showToast(
        "Reciept Text is required. Please configure them in settings.",
        "warning"
      );
      setTimeout(() => {
        navigate("/home/settings");
      }, 3000);
    }
  }, [invoiceSettings, navigate]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await getMyClients();
        setClients(res.data.client || []);
        console.log(res.data.client, "cli");
      } catch (err) {
        console.error("Failed to fetch clients", err);
        showToast("Failed to fetch clients.", "error");
      }
    };

    fetchClients();
  }, []);

  // Get client and company states
  const selectedClient = clients.find((c) => c._id === formData.issuedTo);
  const clientState = selectedClient?.state?.trim().toLowerCase() || "";
  const companyState = company?.state?.trim().toLowerCase() || "";

  const handleCurrentServiceChange = (field, value) => {
    let updated = { ...currentService };

    if (field === "serviceName") {
      const selectedService = servicesList.find((s) => s.name === value);
      if (selectedService) {
        updated = {
          ...updated,
          serviceId: selectedService._id,
          name: selectedService.name,
        };
      } else {
        updated.serviceId = "";
        updated.name = value;
      }
    } else if (field === "description") {
      updated.description = value;
    } else if (field === "qty") {
      const qty = Number(value);
      updated.qty = qty;
      updated.amount = qty * (updated.rate || 0);
    } else if (field === "rate") {
      const rate = Number(value);
      updated.rate = rate;
      updated.amount = rate * (updated.qty || 0);
    }

    setCurrentService(updated);
  };

  const handleAddCurrentService = () => {
    if (!currentService.name || !currentService.qty || !currentService.rate || 
        Number(currentService.qty) <= 0 || Number(currentService.rate) <= 0) {
      showToast("Please fill in all required service fields.", "warning");
      return;
    }

    let updatedServices = [...addedServices];

    if (editingServiceIndex !== null) {
      // Update existing service
      updatedServices[editingServiceIndex] = { ...currentService };
      setEditingServiceIndex(null);
      showToast("Service updated successfully!", "success");
    } else {
      // Add new service
      updatedServices.push({ ...currentService });
      showToast("Service added successfully!", "success");
    }

    setAddedServices(updatedServices);
    setCurrentService({
      serviceId: "",
      name: "",
      description: "",
      rate: "",
      qty: "",
      amount: 0,
    });

    updateTotals(updatedServices, formData.gstIncluded);
    focusAfterServiceOperation('add');
  };

  const handleEditService = (index) => {
    setCurrentService({ ...addedServices[index] });
    setEditingServiceIndex(index);
    
    // Focus the service select when editing
    setTimeout(() => {
      serviceSelectRef.current?.focus();
    }, 100);
  };

  const handleDeleteService = (index) => {
    const updatedServices = addedServices.filter((_, i) => i !== index);
    setAddedServices(updatedServices);
    updateTotals(updatedServices, formData.gstIncluded);

    // If we're editing the service being deleted, clear the current service
    if (editingServiceIndex === index) {
      setCurrentService({
        serviceId: "",
        name: "",
        description: "",
        rate: "",
        qty: "",
        amount: 0,
      });
      setEditingServiceIndex(null);
    }

    showToast("Service deleted successfully!", "success");
    focusAfterServiceOperation('delete');
  };

  const handleCancelEdit = () => {
    setCurrentService({
      serviceId: "",
      name: "",
      description: "",
      rate: "",
      qty: "",
      amount: 0,
    });
    setEditingServiceIndex(null);
    focusAfterServiceOperation('cancel');
  };

  const updateTotals = (services, gstIncluded) => {
    let subtotal = services.reduce((acc, item) => acc + item.amount, 0);

    const isSameState =
      clientState && companyState && clientState === companyState;
    const hasGSTNumber = !!invoiceSettings?.gstNo?.trim();

    let cgstAmount = 0;
    let sgstAmount = 0;
    let igstAmount = 0;
    let totalAmount = subtotal;

    if (hasGSTNumber && gstIncluded) {
      // GST is included in the item rates
      if (isSameState) {
        // Intra-state
        const baseAmount = subtotal / 1.18;
        cgstAmount = baseAmount * 0.09;
        sgstAmount = baseAmount * 0.09;
        subtotal = baseAmount;
        totalAmount = subtotal + cgstAmount + sgstAmount;
      } else {
        // Inter-state
        const baseAmount = subtotal / 1.18;
        igstAmount = baseAmount * 0.18;
        subtotal = baseAmount;
        totalAmount = subtotal + igstAmount;
      }
    } else if (hasGSTNumber && !gstIncluded) {
      // GST is added on top of the item rates
      if (isSameState) {
        cgstAmount = subtotal * 0.09;
        sgstAmount = subtotal * 0.09;
        totalAmount = subtotal + cgstAmount + sgstAmount;
      } else {
        igstAmount = subtotal * 0.18;
        totalAmount = subtotal + igstAmount;
      }
    } else {
      // No GST number — skip GST calculations, total remains subtotal
      console.log("No GST number present. Skipping GST calculations.");
    }

    setFormData((prev) => ({
      ...prev,
      subtotal: parseFloat(subtotal.toFixed(2)),
      cgstAmount: parseFloat(cgstAmount.toFixed(2)),
      sgstAmount: parseFloat(sgstAmount.toFixed(2)),
      igstAmount: parseFloat(igstAmount.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2)),
    }));
  };

  useEffect(() => {
    if (addedServices.length > 0 && (clientState || companyState)) {
      updateTotals(addedServices, formData.gstIncluded);
    }
  }, [formData.issuedTo, clientState, companyState, formData.gstIncluded]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    setIsSaving(true);  

    const payload = {
      invoiceNumber: formData.invoiceNumber,
      issuedToType: formData.issuedToType,
      issuedTo: formData.issuedTo,
      items: addedServices.map((srv) => ({
        name: srv.name,
        description: srv.description,
        qty: srv.qty,
        rate: srv.rate,
        amount: srv.amount,
      })),
      subtotal: formData.subtotal,
      cgstAmount: formData.cgstAmount,
      sgstAmount: formData.sgstAmount,
      igstAmount: formData.igstAmount,
      cgstPercentage: formData.cgstAmount > 0 ? 9 : 0,
      sgstPercentage: formData.sgstAmount > 0 ? 9 : 0,
      igstPercentage: formData.igstAmount > 0 ? 18 : 0,
      totalAmount: formData.totalAmount,
    };

    console.log(payload, "payloadpayloadpayloadpayloadpayloadinvoice");
    
    try {
      await addInvoiceCompany(payload);
      setGlobalSettings((prev) => {
        const updated = { ...prev };
        if (updated?.result?.[0]?.invoiceSettings?.predefinedNo) {
          updated.result[0].invoiceSettings.predefinedNo.count =
            Number(predefinedCount) + 1;
        }
        return updated;
      });

      showToast("Invoice saved successfully!", "success");
      navigate("/home/invoices");
    } catch (err) {
      console.error("Error adding invoice:", err);
      showToast("Failed to add invoice. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddNewService = async () => {
    if (!newServiceName.trim()) return;

    try {
      setIsAddingService(true);

      const newService = { name: newServiceName.trim() };
      const cleanedExisting = (invoiceSettings?.services || []).map((s) => ({
        name: s.name.trim(),
        _id: s._id,
      }));

      const isDuplicate = cleanedExisting.some(
        (s) => s.name.toLowerCase() === newService.name.toLowerCase()
      );
      if (isDuplicate) {
        showToast("Service with this name already exists!", "warning");
        return;
      }

      const updatedServices = [...cleanedExisting, newService];

      const payload = { services: updatedServices };
      await updateInvoiceSettings(payload);

      setGlobalSettings((prev) => {
        const updated = { ...prev };
        if (updated?.result?.[0]?.invoiceSettings) {
          updated.result[0].invoiceSettings.services = updatedServices;
        }
        return updated;
      });

      setNewServiceName("");
      showToast("Service added successfully!");
      setShowServiceModal(false);
      
      // Focus back to service select after adding new service
      setTimeout(() => {
        serviceSelectRef.current?.focus();
      }, 200);
    } catch (err) {
      console.error("Failed to add service:", err);
      showToast("Failed to add service.", "error");
    } finally {
      setIsAddingService(false);
    }
  };

  // Modal input keydown handler
  const handleModalKeyDown = (e) => {
    if (e.key === 'Enter' && newServiceName.trim() && !isAddingService) {
      e.preventDefault();
      handleAddNewService();
    }
  };

  const isFormValid = () => {
    if (!formData.issuedTo || formData.issuedTo === "") {
      return false;
    }
    return addedServices.length > 0;
  };

  const isCurrentServiceValid = () => {
    return (
      currentService.name &&
      currentService.qty &&
      Number(currentService.qty) > 0 &&
      currentService.rate &&
      Number(currentService.rate) > 0
    );
  };

  return (
    <div className="invoice-form-container">
      <div className="invoice-form-wrapper">
        <div className="invoice-form-header">
          <h1 className="invoice-form-title">Create New Invoice</h1>
        </div>

        <hr className="invoice-form-divider" />

        <form className="invoice-form-content" onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
          <div className="form-section">
            <h3>Invoice Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="invoiceNumber">Invoice Number:</label>
                <input
                  type="text"
                  id="invoiceNumber"
                  value={formData.invoiceNumber}
                  disabled
                />
              </div>
              <div className="form-group">
                <label htmlFor="issuedTo">Issued To:</label>
                <select
                  ref={issuedToRef}  // Add ref here
                  id="issuedTo"
                  value={formData.issuedTo}
                  onChange={(e) => {
                    if (e.target.value === "__add_new__") {
                      setShowClientModal(true);
                    } else {
                      setFormData({ ...formData, issuedTo: e.target.value });
                    }
                  }}
                  required
                >
                  <option value="">-- Select Client --</option>
                  {clients.map((client) => (
                    <option key={client._id} value={client._id}>
                      {client.name}
                    </option>
                  ))}
                  <option value="__add_new__">+ Add New Client</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>
              Services <span className="invoice-form-required">*</span>
            </h3>

            {/* Added Services Cards */}
            {addedServices.length > 0 && (
              <div className="added-services-container">
                {addedServices.map((service, index) => (
                  <div className="service-card" key={index}>
                    <div className="service-card-content">
                      <div className="service-card-main">
                        <div className="service-info">
                          <h4>{service.name}</h4>
                          {service.description && (
                            <p className="service-description">
                              {service.description}
                            </p>
                          )}
                        </div>
                        <div className="service-details">
                          <span>Qty: {service.qty}</span>
                          <span>Rate: ₹{service.rate}</span>
                          <span className="service-amount">
                            ₹{service.amount.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="service-card-actions">
                        <button
                          type="button"
                          className="edit-service-btn"
                          onClick={() => handleEditService(index)}
                          disabled={!formData.issuedTo}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="delete-service-btn"
                          onClick={() => handleDeleteService(index)}
                          disabled={!formData.issuedTo}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Current Service Form */}
            <div className="current-service-form">
              <div className="service-form-header">
                <h4>
                  {editingServiceIndex !== null
                    ? "Edit Service"
                    : addedServices.length === 0
                    ? "Add Your First Service"
                    : "Add Another Service"}
                </h4>
              </div>

              <div className="service-form-row">
                <div className="form-group">
                  <label>
                    Service <span className="invoice-form-required">*</span>
                  </label>
                  <select
                    ref={serviceSelectRef}  // Add ref here
                    value={currentService.name}
                    onChange={(e) => {
                      if (e.target.value === "__add_new_service__") {
                        setShowServiceModal(true);
                      } else {
                        handleCurrentServiceChange("serviceName", e.target.value);
                      }
                    }}
                    disabled={!formData.issuedTo}
                  >
                    <option value="">-- Select Service --</option>
                    {servicesList.map((srv) => (
                      <option key={srv._id} value={srv.name}>
                        {srv.name}
                      </option>
                    ))}
                    <option value="__add_new_service__">+ Add New Service</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    Quantity <span className="invoice-form-required">*</span>
                  </label>
                  <input
                    ref={quantityInputRef}  // Add ref here
                    type="number"
                    placeholder="Qty"
                    min="1"
                    value={currentService.qty}
                    onChange={(e) =>
                      handleCurrentServiceChange("qty", e.target.value)
                    }
                    onWheel={(e) => e.target.blur()}
                    disabled={!formData.issuedTo}
                    className="no-spinner"
                  />
                </div>

                <div className="form-group">
                  <label>
                    Rate <span className="invoice-form-required">*</span>
                  </label>
                  <input
                    ref={rateInputRef}  // Add ref here
                    type="number"
                    placeholder="Rate"
                    min="0"
                    step="0.01"
                    value={currentService.rate}
                    onChange={(e) =>
                      handleCurrentServiceChange("rate", e.target.value)
                    }
                    onWheel={(e) => e.target.blur()}
                    disabled={!formData.issuedTo}
                    className="no-spinner"
                  />
                </div>

                <div className="form-group">
                  <label>Amount</label>
                  <div className="amount-display">
                    ₹{currentService.amount.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="service-description-row">
                <div className="form-group description-group">
                  <label>Description</label>
                  <input
                    ref={descriptionInputRef}  // Add ref here
                    type="text"
                    placeholder="Service description"
                    value={currentService.description}
                    onChange={(e) =>
                      handleCurrentServiceChange("description", e.target.value)
                    }
                    disabled={!formData.issuedTo}
                  />
                </div>
              </div>

              <div className="service-form-actions">
                <button
                  type="button"
                  onClick={handleAddCurrentService}
                  disabled={!formData.issuedTo || !isCurrentServiceValid()}
                  className="add-current-service-btn"
                >
                  {editingServiceIndex !== null ? "Update Service" : "Add Service"}
                </button>
                {editingServiceIndex !== null && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="cancel-edit-btn"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Invoice Summary</h3>
            <div className="summary">
              <p>
                <strong>Subtotal:</strong> ₹{formData.subtotal.toFixed(2)}
              </p>
              <p>
                <strong>CGST (9%):</strong> ₹{formData.cgstAmount.toFixed(2)}{" "}
                {!invoiceSettings?.gstNo?.trim() && <span>(No GST Number)</span>}
              </p>
              <p>
                <strong>SGST (9%):</strong> ₹{formData.sgstAmount.toFixed(2)}{" "}
                {!invoiceSettings?.gstNo?.trim() && <span>(No GST Number)</span>}
              </p>
              <p>
                <strong>IGST (18%):</strong> ₹{formData.igstAmount.toFixed(2)}{" "}
                {!invoiceSettings?.gstNo?.trim() && <span>(No GST Number)</span>}
              </p>
              <p>
                <strong>Total Amount:</strong> ₹{formData.totalAmount.toFixed(2)}
              </p>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={isSaving || !isFormValid()}
            >
              {isSaving ? "Saving Invoice..." : "Save Invoice"}
            </button>
          </div>
        </form>

        {/* Modals */}
        {showClientModal && (
          <CommonModal onClose={() => setShowClientModal(false)}>
            <div className="client-modal-header-inv">
              <button
                className="close-cross"
                onClick={() => setShowClientModal(false)}
              >
                ×
              </button>
            </div>
            <MyClientForm
              onSuccess={() => {
                setShowClientModal(false);
                getMyClients().then((res) => setClients(res.data.client || []));
              }}
              onCancel={() => setShowClientModal(false)}
            />
          </CommonModal>
        )}
        {/* MODIFICATION: Replaced CommonModal with a new structure */}
        {showServiceModal && (
          <div className="confirmation-modal-backdrop">
            <div className="confirmation-modal notinvoice" ref={addServiceModalRef}>
              <button
                className="close-cross"
                onClick={() => setShowServiceModal(false)}
                aria-label="Close"
              >
                &times;
              </button>
              
              <div className="add-new-field-modal-content">
                <h3>Add New Service</h3>
                <input
                  type="text"
                  placeholder="Enter service name"
                  value={newServiceName}
                  onChange={(e) => setNewServiceName(e.target.value)}
                  onKeyDown={handleModalKeyDown}
                  className="add-new-field-input"
                  autoFocus
                />
              </div>

              <div className="confirmation-actions notinvoice">
                {/* <button
                  className="cancel-btn"
                  onClick={() => setShowServiceModal(false)}
                  disabled={isAddingService}
                >
                  Cancel
                </button> */}
                <button
                  className="confirm-btn"
                  onClick={handleAddNewService}
                  disabled={!newServiceName.trim() || isAddingService}
                >
                  {isAddingService ? "Adding..." : "Add Service"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};